import { useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, type QueryKey } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { History, Sparkles } from 'lucide-react'
import { CardContent, CardIcon } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { DateCell, NameCell } from '@/components/TableCells'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { useNetwork } from '../contexts/NetworkContext'
import { getContext } from '@/services/networks/networksApi'
import { AuthService } from '@/services/core/auth'

export type RecentSort = 'createTime' | 'updateTime'

interface RecentObject {
  id: string
  name: string
  createTime: string
  updateTime: string
}

interface RecentObjectsCardProps<T extends RecentObject> {
  title: ReactNode
  /** Newest created, or most recently changed. Also picks the icon. */
  sort: RecentSort
  /**
   * Leading part of the query key. The network, sort and limit are added, so
   * this names the object — start it with the key its list page invalidates,
   * so a save there refreshes this card too.
   */
  queryKey: QueryKey
  fetch: (sort: RecentSort, limit: number) => Promise<T[]>
  href: (row: T) => string
  /** Second line under the name, e.g. the owning advertiser or site. */
  sub?: (row: T) => string | null | undefined
  /** Columns between the name and the date, e.g. a status. */
  columns?: ColumnDef<T, unknown>[]
  limit?: number
}

/** The latest objects of one kind, newest first — a way back to what was just worked on. */
export function RecentObjectsCard<T extends RecentObject>({
  title,
  sort,
  queryKey,
  fetch,
  href,
  sub,
  columns: middle = [],
  limit = 6,
}: RecentObjectsCardProps<T>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scopeKey, scopeReady } = useNetwork()
  const context = getContext()

  const { data = [], isLoading } = useQuery({
    queryKey: [...queryKey, 'recent', sort, limit, context, scopeKey],
    queryFn: () => fetch(sort, limit),
    enabled: AuthService.isAuthenticated() && scopeReady,
  })

  const columns = useMemo<ColumnDef<T, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common.name'),
        meta: { minWidth: 180 },
        cell: ({ row }) => <NameCell name={row.original.name} sub={sub?.(row.original)} />,
      },
      ...middle,
      {
        accessorKey: sort,
        header: sort === 'createTime' ? t('common.created') : t('common.updated'),
        size: 150,
        cell: ({ row }) => <DateCell value={row.original[sort]} withTime />,
      },
    ],
    [t, sort, sub, middle],
  )

  const Icon = sort === 'createTime' ? Sparkles : History

  return (
    <ListCard>
      <ListCardHeader
        title={
          <>
            <CardIcon icon={Icon} />
            {title}
          </>
        }
      />
      <CardContent className="p-0">
        <DataTable
          columns={columns}
          data={data}
          loading={isLoading}
          skeletonRows={limit}
          inset
          emptyMessage={t('common.noResults')}
          onRowClick={(row) => navigate(href(row))}
        />
      </CardContent>
    </ListCard>
  )
}
